import axios from 'axios';

const api = axios.create({
  baseURL: '/api',
  headers: { 'Content-Type': 'application/json' },
});

// Attach JWT token
api.interceptors.request.use(config => {
  const token = localStorage.getItem('token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// Handle 401 globally (skip auth endpoints so login errors surface normally)
api.interceptors.response.use(
  res => res,
  err => {
    const url = err.config?.url || '';
    const isAuthEndpoint = url.startsWith('/auth/');
    if (err.response?.status === 401 && !isAuthEndpoint) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(err);
  }
);

// Auth
export const login = data => api.post('/auth/login', data);
export const register = data => api.post('/auth/register', data);
export const getMe = () => api.get('/auth/me');
export const changePassword = data => api.put('/auth/change-password', data);

// Customers
export const getCustomers = params => api.get('/customers', { params });
export const getCustomer = id => api.get(`/customers/${id}`);
export const createCustomer = data => api.post('/customers', data);
export const updateCustomer = (id, data) => api.put(`/customers/${id}`, data);
export const deleteCustomer = id => api.delete(`/customers/${id}`);

// Products / Inventory
export const getProducts = params => api.get('/products', { params });
export const getProduct = id => api.get(`/products/${id}`);
export const createProduct = data => api.post('/products', data);
export const updateProduct = (id, data) => api.put(`/products/${id}`, data);
export const deleteProduct = id => api.delete(`/products/${id}`);
export const updateStock = (id, data) => api.post(`/products/${id}/stock`, data);
export const getInventoryLogs = id => api.get(`/products/${id}/logs`);

// Orders
export const getOrders = params => api.get('/orders', { params });
export const getOrder = id => api.get(`/orders/${id}`);
export const createOrder = data => api.post('/orders', data);
export const updateOrder = (id, data) => api.put(`/orders/${id}`, data);
export const updateOrderStatus = (id, status) => api.put(`/orders/${id}/status`, { status });
export const deleteOrder = id => api.delete(`/orders/${id}`);

// Employees
export const getEmployees = params => api.get('/employees', { params });
export const getEmployee = id => api.get(`/employees/${id}`);
export const createEmployee = data => api.post('/employees', data);
export const updateEmployee = (id, data) => api.put(`/employees/${id}`, data);
export const deleteEmployee = id => api.delete(`/employees/${id}`);

// Invoices
export const getInvoices = params => api.get('/invoices', { params });
export const getInvoice = id => api.get(`/invoices/${id}`);
export const createInvoice = data => api.post('/invoices', data);
export const updateInvoice = (id, data) => api.put(`/invoices/${id}`, data);
export const deleteInvoice = id => api.delete(`/invoices/${id}`);
export const recordPayment = (id, data) => api.post(`/invoices/${id}/payment`, data);

// Deliveries
export const getDeliveries = params => api.get('/deliveries', { params });
export const getDelivery = id => api.get(`/deliveries/${id}`);
export const createDelivery = data => api.post('/deliveries', data);
export const updateDelivery = (id, data) => api.put(`/deliveries/${id}`, data);
export const deleteDelivery = id => api.delete(`/deliveries/${id}`);

// Gallery
export const getGallery = (category) => api.get('/gallery', { params: category ? { category } : {} });
export const getGalleryManage = () => api.get('/gallery/manage');
export const uploadGalleryImage = (formData) => api.post('/gallery', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
export const updateGalleryImage = (id, data) => api.put(`/gallery/${id}`, data);
export const deleteGalleryImage = (id) => api.delete(`/gallery/${id}`);

// Reports
export const getDashboardStats = () => api.get('/reports/dashboard');
export const getSalesReport = params => api.get('/reports/sales', { params });
export const getCollectionsReport = params => api.get('/reports/collections', { params });
export const getInventoryReport = () => api.get('/reports/inventory');
export const getCustomersReport = () => api.get('/reports/customers');

export default api;
